import { differenceInCalendarDays, format, parseISO } from 'date-fns'

import { EMPTY_LIST } from '../../../../constants/common'
import { DATE_SHORT_FORMAT } from '../../../../constants/date'
import { RENTAL_PORTAL_FACT_SEPARATOR } from '../../../../constants/rentalPortal'
import { PhaseItemStatus } from '../../../Entity'
import { APPLICANT_STAGES } from '../../../RentalPortalApplicant'
import { getRentalPortalApplicantPortalLabel } from '../../RentalPortalApplicantPortalCell'
import { APPLICANT_TERMINAL_STAGES } from '../RentalPortalApplicantProfile.constants'
import { getIsApplicantClosed } from '../RentalPortalApplicantProfile.utils'

import {
  APPLICANT_FOLLOW_UP_SOON_DAYS,
  APPLICANT_FOLLOW_UP_TEXT_COLOR,
  APPLICANT_PHASE_STATE,
  APPLICANT_PHASE_TERMINAL_STATEMENT_KEY,
  APPLICANT_PROFILE_INFO_ITEM_ACTION_KEY,
  QA_NAME_APPLICANT_PROFILE_INFO_RESPONSIBLE_MANAGER_ADD,
  QA_NAME_APPLICANT_PROFILE_INFO_RESPONSIBLE_MANAGER_EDIT,
} from './RentalPortalApplicantProfileHeaderInfoItems.constants'

function getApplicantPhaseItemStatus(state) {
  switch (state) {
    case APPLICANT_PHASE_STATE.done:
      return PhaseItemStatus.finished
    case APPLICANT_PHASE_STATE.active:
      return PhaseItemStatus.inProgress
    default:
      return PhaseItemStatus.notStarted
  }
}

function getApplicantFollowUpDaysLeftTextColor(value) {
  const daysLeft = differenceInCalendarDays(parseISO(value), new Date())

  if (daysLeft < 0) {
    return APPLICANT_FOLLOW_UP_TEXT_COLOR.past
  }

  return daysLeft <= APPLICANT_FOLLOW_UP_SOON_DAYS
    ? APPLICANT_FOLLOW_UP_TEXT_COLOR.soon
    : APPLICANT_FOLLOW_UP_TEXT_COLOR.future
}

export function getApplicantShortDate(value) {
  return value ? format(parseISO(value), DATE_SHORT_FORMAT) : null
}

export function getApplicantPhaseItems({ isTerminal, phases, t }) {
  return (phases || EMPTY_LIST).map(({ key, state }) => ({
    id: key,
    name: APPLICANT_STAGES[key] ? t(APPLICANT_STAGES[key].text) : null,
    status: isTerminal
      ? PhaseItemStatus.notStarted
      : getApplicantPhaseItemStatus(state),
  }))
}

export function getApplicantPhaseTerminalStatement({ phase, t, terminalDate }) {
  const statementKey =
    APPLICANT_PHASE_TERMINAL_STATEMENT_KEY[phase?.terminal_reason]

  return statementKey && terminalDate
    ? t(statementKey, { date: getApplicantShortDate(terminalDate) })
    : t('RentalPortal.ListingPhaseTerminalNoActionKey')
}

export function getIsApplicantTerminal(applicantData) {
  return (
    applicantData?.archived ||
    APPLICANT_TERMINAL_STAGES.includes(applicantData?.stage)
  )
}

export function getApplicantFollowUpTextColor({ isTerminal, value }) {
  if (value) {
    return isTerminal
      ? APPLICANT_FOLLOW_UP_TEXT_COLOR.terminal
      : getApplicantFollowUpDaysLeftTextColor(value)
  }
}

export function getApplicantPortalReceivedValue({ applicantData, t }) {
  return [
    getRentalPortalApplicantPortalLabel({ applicant: applicantData, t }),
    getApplicantShortDate(applicantData?.received),
  ]
    .filter(Boolean)
    .join(RENTAL_PORTAL_FACT_SEPARATOR)
}

export function getCanEditApplicantFollowUp({ applicantData, isPreview }) {
  return Boolean(
    !isPreview &&
    !getIsApplicantClosed(applicantData) &&
    applicantData?.permissions?.can_edit_content
  )
}

export function getCanEditApplicantResponsibleManager({
  applicantData,
  isPreview,
}) {
  return Boolean(
    !isPreview &&
    !getIsApplicantClosed(applicantData) &&
    applicantData?.permissions?.can_edit_responsible_manager
  )
}

export function toApplicantProfileUserValue(user) {
  return user
    ? {
        archived: user.is_archived,
        avatarColor: user.avatar_color,
        email: user.email,
        fullname: user.full_name,
        id: user.id,
      }
    : null
}

export function getApplicantProfileHeaderInfoItemActions(onClick) {
  return {
    [APPLICANT_PROFILE_INFO_ITEM_ACTION_KEY.add]: { onClick },
    [APPLICANT_PROFILE_INFO_ITEM_ACTION_KEY.edit]: { onClick },
  }
}

export function getApplicantProfileManagerItemProps({
  actionButtonClassName,
  actionsWrapperClassName,
  canEdit,
}) {
  return {
    actions: canEdit ? undefined : null,
    canEdit,
    itemActionsProps: canEdit
      ? {
          actionsWrapperClassName,
          buttonsProps: {
            [APPLICANT_PROFILE_INFO_ITEM_ACTION_KEY.add]: {
              className: actionButtonClassName,
              qaName: QA_NAME_APPLICANT_PROFILE_INFO_RESPONSIBLE_MANAGER_ADD,
            },
            [APPLICANT_PROFILE_INFO_ITEM_ACTION_KEY.edit]: {
              className: actionButtonClassName,
              qaName: QA_NAME_APPLICANT_PROFILE_INFO_RESPONSIBLE_MANAGER_EDIT,
            },
          },
        }
      : undefined,
  }
}
